import { useCallback, useEffect, useMemo } from 'react'
import cn from 'classnames'
import { useShallow } from 'zustand/react/shallow'
import Loader from 'components/Loader'
import Sidebar from 'components/Sidebar'
import Hint from 'components/Wizard/Hint'
import AttributeSidebarContent from './AttributeSidebarContent'
import useRelationsContent from 'hooks/api/useRelationsContent'
import { capitalize, optionsParser, t } from 'helpers'
import { useAppStore, usePortfolioStore, useUserStore } from 'store'
import { PORTFOLIO_TYPES, RELATIONS } from 'types/enums'
import { AttributeCardType, ValueLevel } from 'types/app'
import {
	attributeBasicFields,
	attributeSpecialFields,
	skillFields,
} from 'pages/Portfolio/fields'
import s from './index.module.scss'
import { contentDelete, partialContentUpdate } from 'api/content'

interface Props {
	cardData: AttributeCardType | null
}

const AttributeSidebar = ({ cardData }: Props): JSX.Element => {
	const [loading, type, generalType, iconClose] = usePortfolioStore(
		useShallow((state) => [
			state.loading,
			state.modalType,
			state.generalModalType,
			!!state.additionalSidebar ||
				!!state.galleryType ||
				!!state.mediaZoomSidebar?.url,
		]),
	)
	const { uploadImageData } =usePortfolioStore.getState()
	const userId = useUserStore(useShallow((state) => state.userId))
	const { data } = useRelationsContent(
		[RELATIONS.IMAGE, RELATIONS.BEST_PERFORMANCES],
		userId,
		undefined,
		'',
		false,
		true,
	)

	const images = useMemo(
		() =>
			data
				? Object.entries(data)
						?.filter(([key]) => RELATIONS.IMAGE === key)
						?.map((value) => value?.[1])
						.flat()
				: [],
		[data],
	)

	const videos = useMemo(
		() =>
			data
				? Object.entries(data)
						?.filter(([key]) => RELATIONS.BEST_PERFORMANCES === key)
						?.map((value) => value?.[1])
						.flat()
				: [],
		[data],
	)

	const values = usePortfolioStore(
		useShallow((state) => state.cardValues[type] || {}),
	)

	const selectorsData = useMemo(
		() =>
			cardData?.selectors?.map((select) => ({
				...select,
				options: optionsParser(t(select.optionName || '')),
			})),
		[cardData?.selectors],
	)

	const hideVoiceSelectors = useMemo(
		() =>
			cardData?.value === 'voice' &&
			(values?.[cardData?.selectors?.[0].name] as string[])?.[0] ===
				optionsParser(cardData?.selectors?.[0]?.optionName || '')?.[0]?.value,
		[cardData?.selectors, cardData?.value, values],
	)

	const mediaType = [
		PORTFOLIO_TYPES.PICTURE,
		PORTFOLIO_TYPES.SHOWREEL,
	].includes(generalType)

	const closeSidebar = (): void => {
		const {
			clearModalType,
			setGalleryType,
			setCardData,
			setAdditionalSidebar,
		} = usePortfolioStore.getState()
		clearModalType()
		setGalleryType(null)
		setCardData(null)
		setAdditionalSidebar(null)
	}

	const assignedMedia = useMemo(
		() =>
			images
				?.concat(videos)
				.map((content) => {
					const values = [
						content?.ContentLabelAttribute || '',
						content?.ContentLabelSkill || '',
					]
						?.filter((v) => !!v)
						.join(',')
						.split(',')
						?.filter((v) => !!v)

					return { [content.ContentId]: values }
				})
				.reduce(
					(prev, next) => ({
						...prev,
						...next,
					}),
					{},
				),
		[images, videos],
	)

	useEffect(() => {
		const { galleryValues, setGalleryItems } = usePortfolioStore.getState()
		if (
			Object.values(assignedMedia)?.length &&
			!Object.values(galleryValues)?.length
		) {
			setGalleryItems(assignedMedia)
		}
	}, [assignedMedia])

	const onDone = async (
		next?: boolean,
		cardData?: AttributeCardType | null,
	): Promise<void> => {
		if (
			[PORTFOLIO_TYPES.SHOWREEL, PORTFOLIO_TYPES.PICTURE].includes(generalType)
		) {
			await uploadFileWithTags()
			if (next && cardData) {
				closeSidebar()
				setTimeout(() => {
					findNextEmpty(cardData)
				}, 500)
			} else closeSidebar()
		} else {
			const { cardValues, setCardValues, modalType, updateFields } =
				usePortfolioStore.getState()
			const values = cardValues?.[modalType]
			const parsed = Object.entries(values)?.map(([name, v]) => {
				const value = v as ValueLevel[]
				if (value && Array.isArray(value)) {
					if (typeof value?.[0] === 'string') {
						return {
							Name: name,
							Value: JSON.stringify(value) as string,
						}
					} else {
						const newValues: ValueLevel[] = []
						value?.forEach((v) => {
							// const { galleryValues } = usePortfolioStore.getState()
							// const item = Object.entries(galleryValues).find((values) =>
							// 	values?.[1]?.includes(name + ':' + v?.Value),
							// )?.[0]
							// if (v?.Level && !item) {
							// 	newValues.push({
							// 		Value: v.Value,
							// 		Level: '',
							// 	})
							// } else if (v.Value) newValues.push(v)
							if (v.Value) newValues.push(v)
						})

						setCardValues({
							[name]: newValues,
						})

						return {
							Name: name,
							Value: JSON.stringify(newValues) as string,
						}
					}
				} else
					return {
						Name: name,
						Value: value as string,
					}
			})
			await updateFields(parsed)

			if (next) {
				closeSidebar()
				setTimeout(() => {
					openNext(type)
				}, 500)
			} else closeSidebar()
			// else closeSidebar()
			// else {
			// const { setGalleryType } = usePortfolioStore.getState()
			// setGalleryType(null)
			// }
		}
	}

	const checkNextAttribute = (
		currentAttribute: string,
		data: AttributeCardType[],
	): null | AttributeCardType | number => {
		const currentIndex = data.findIndex(
			(field) => field.value === currentAttribute,
		)
		const nextField = data?.[currentIndex + 1]
		if (currentIndex >= 0 && nextField) return nextField
		else if (currentIndex >= 0) return currentIndex
		else return null
	}

	const setAttributeSidebar = (
		generalType: PORTFOLIO_TYPES,
		modalType: string,
		cardData: AttributeCardType,
	): void => {
		const { setModalType, setGeneralModalType, setCardData } =
			usePortfolioStore.getState()
		setGeneralModalType(generalType)
		setModalType(modalType)
		setCardData(cardData)
	}

	const openNext = (currentAttribute: string): void => {
		const attributes = Object.values({
			...attributeBasicFields,
			...attributeSpecialFields,
		})
		const attribute = checkNextAttribute(currentAttribute, attributes)
		if (attribute && typeof attribute !== 'number') {
			setAttributeSidebar(PORTFOLIO_TYPES.ATTRIBUTE, attribute.value, attribute)
		} else if (!attribute || typeof attribute === 'number') {
			const skills = Object.values(skillFields)
			if (typeof attribute === 'number') {
				setAttributeSidebar(
					PORTFOLIO_TYPES.SKILL,
					skills?.[0]?.value,
					skills?.[0],
				)
				const { stepWizard } = useAppStore.getState()
				stepWizard?.nextStep()
			} else {
				const skill = checkNextAttribute(currentAttribute, skills)
				if (skill && typeof skill !== 'number')
					setAttributeSidebar(PORTFOLIO_TYPES.SKILL, skill.value, skill)
				else closeSidebar()
			}
		} else closeSidebar()
	}

	const findNextEmpty = useCallback((cardData: AttributeCardType): void => {
		const max = 16
		const array = Array.from({ length: max })?.map((_, index) => index)
		const { cardValues, generalModalType, setModalType, setCardData,setuploadImage,uploadImageData } =usePortfolioStore.getState()
		const index = cardData?.index || uploadImageData?.index || 0
		const indexes = array.slice(index + 1, max).concat(array.slice(0, index))
		const nextEmptyIndex = indexes.find((ind) => {
			const propName = generalModalType + ind
			const values = cardValues[propName]

			return !values ? generalModalType + '-' + ind : null
		})
		if (nextEmptyIndex !== undefined) {
			const { stepWizard } = useAppStore.getState()
			const nextStep = Math.floor(nextEmptyIndex / 4)
			stepWizard?.goToNamedStep(`${generalModalType + 's'}-${nextStep}`)
			setTimeout(() => {
				setModalType(generalModalType + nextEmptyIndex)
				setCardData({
					...cardData,
					index: nextEmptyIndex,
				})
				if(uploadImageData){
					setuploadImage({
						...uploadImageData,
						index: nextEmptyIndex,
					})
				}
			}, 350)
		} else{
			 setCardData(null)
			 setuploadImage(null)
			}
	}, [])

	const uploadFileWithTags = useCallback(async (): Promise<void> => {
		const { setLoading } = usePortfolioStore.getState()

		try {
			const {
				cardValues,
				getModalProp,
				uploadFile,
				generalModalType,
				setCardValues,
				uploadImageData,
				setuploadImage
			} = usePortfolioStore.getState()
			const propName = getModalProp()
			const values = cardValues[propName]

			setLoading(true)
			const file = values && values[propName]
			let contentId: string | undefined = ''
			if (file && file instanceof File && values?.newUpload) {
			const mediaIndex = cardData?.index || uploadImageData?.index
				contentId = await uploadFile(
					file.name,
					file as File,
					generalModalType === PORTFOLIO_TYPES.SHOWREEL ? 'Video' : 'Image',
					mediaIndex,
				)
				setCardValues({
					newUpload: false,
					contentId: contentId || '',
				})
				if (values?.deleteId) contentDelete(values.deleteId as string)
			}
			const id = values?.newUpload ? contentId : (values?.contentId as string)
			if (id) {
				const skills = Object.keys(skillFields)?.map((name) =>
					name.toLowerCase(),
				)
				const attributes = Object.keys({
					...attributeBasicFields,
					...attributeSpecialFields,
				})?.map((name) => name.toLowerCase())
				const skillTags = [
					...new Set(
						((values?.tags as unknown as string[][]) || [[]]).map((tags) =>
							skills.includes(tags?.[0]?.toLowerCase())
								? `${tags[0]}:${tags[1]}`
								: '',
						),
					),
				].filter((v) => !!v)
				const attrTags = [
					...new Set(
						((values?.tags as unknown as string[][]) || [[]]).map((tags) =>
							attributes.includes(tags?.[0]?.toLowerCase())
								? `${tags[0]}:${tags[1]}`
								: '',
						),
					),
				].filter((v) => !!v)
				await partialContentUpdate(
					id,
					{
						labels: {
							Style: [
								Object.entries(values).find(([key]) =>
									key.toLowerCase().includes('style'),
								)?.[1],
							],
							Type: [
								Object.entries(values).find(([key]) =>
									key.toLowerCase().includes('type'),
								)?.[1],
							],
							[capitalize(PORTFOLIO_TYPES.ATTRIBUTE)]: attrTags,
							[capitalize(PORTFOLIO_TYPES.SKILL)]: skillTags,
						},
					},
					'?strategy=1',
				)
			}
		} catch (error) {
			console.log('error:', error)
		} finally {
			setLoading(false)
		}
	},[uploadImageData?.index])

	return (
		<Sidebar
			open={!!cardData}
			onClose={() => {
				closeSidebar()
			}}
			iconClose={iconClose}
			header={{
				title: mediaType
					? t(`APP_${generalType?.toUpperCase()}_CARD`) +
					  ` #${(cardData?.index || uploadImageData?.index || 0) + 1}`
					: t(
							`APP_${
								type?.toUpperCase() + '_'
							}${generalType?.toUpperCase()}_MODAL_TITLE`,
					  ),
			}}
			buttons={[
				{
					children: t('APP_DONE_NEXT'),
					onClick: async () => {
						onDone(true, cardData)
						// const { setPrevSidebarData, prevSidebarData } =
						// usePortfolioStore.getState()
						// setPrevSidebarData({
						// cardData,
						// })
						// closeSidebar()

						// setTimeout(() => {
						// openNext(type)
						// }, 500)
						// openNext(type)
					},
				},
				{
					children: t('APP_DONE'),
					onClick: () => onDone(),
				},
			]}>
			<>
				<Loader loading={loading} adapt />
				{cardData && selectorsData ? (
					<>
						<Hint
							hintText={t(
								`APP_${(mediaType
									? generalType
									: type
								).toUpperCase()}_MODAL_HINT`,
							)}
							style={{ marginTop: '0' }}
						/>
						<div className={cn(s.contentWrapper, 'hideScrollBar')}>
							<AttributeSidebarContent
								cardData={cardData}
								selectorsData={
									hideVoiceSelectors
										? selectorsData?.slice(0, 1)
										: selectorsData
								}
							/>
						</div>
					</>
				) : null}
			</>
		</Sidebar>
	)
}

export default AttributeSidebar
